import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/data';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 蓝莓视频"
          description="抱歉，您查找的文章不存在或已被删除。"
          keywords="蓝莓视频"
          canonicalUrl="https://lanmei.video/article"
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您查找的文章不存在或已被删除。</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '蓝莓视频',
    },
    image: article.coverImage,
    url: `https://lanmei.video/article/${article.id}`,
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 蓝莓视频资讯`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},蓝莓视频,水果视频,影视资讯`}
        canonicalUrl={`https://lanmei.video/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article>
          <div className="w-full px-4 md:px-6 pt-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '资讯', href: '/category/dianshiju' },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 pt-6">
            <div className="max-w-4xl mx-auto">
              <div className="aspect-[21/9] rounded-xl overflow-hidden mb-6">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-foreground-600 text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-foreground-600 text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-foreground-600 text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100 mt-10">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} {...a} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}